/* eslint-disable import/prefer-default-export */
import {
  useRef, useEffect, useState, useCallback,
} from 'react';
import debounce from '../Utils/debouncer';
import { getRandomId } from '../Utils/baseComponentHelper';

const NOOP = () => { };

/**
 * Force to re-render the component.
 */
export function useForceUpdate() {
  const [, updateState] = useState();
  return useCallback(() => updateState({}), []);
}

/**
 * Create a new element as a body child.
 */
export function usePortalElement(create, id = 'modal-element', doc = document) {
  const portalRef = useRef();
  if (portalRef.current === undefined) {
    portalRef.current = doc.createElement('div');
    portalRef.current.setAttribute('id', id);
  }
  useEffect(() => {
    if (!portalRef.current) return () => { };
    doc.body.appendChild(portalRef.current);
    return () => {
      doc.body.removeChild(portalRef.current);
      portalRef.current = undefined;
    };
  }, [doc]);
  return portalRef;
}

function calcStyle(y, ref, height, diff = 180) {
  if (ref && ref.current && (y + ref.current.clientHeight) >= height) {
    return { overflow: 'auto', maxHeight: `${height - diff}px` };
  }
  return { overflow: 'none', maxHeight: 'auto' };
}

function getSize() {
  return {
    width: window.innerWidth || document.documentElement.clientWidth,
    height: window.innerHeight || document.documentElement.clientHeight,
  };
}

function createResizeWindowCallback(x, y, mouseCoord, setX, setY) {
  return () => {
    const { width, height } = getSize();
    const { width: mouseWidth, height: mouseHeight } = mouseCoord;
    let posX = x;
    let posY = y;
    if (Number.isInteger(posX) && mouseWidth) {
      posX = Math.max(posX + mouseWidth > width ? width - mouseWidth : posX, 0);
      posY = Math.max(posY + mouseHeight > height ? height - mouseHeight : posY, 0);
    }
    setX(posX); setY(posY);
  };
}

/**
 * Change modal position.
 * Handle mouse move and window resize to return the new modal position.
 * Return
 * the new postion (x,y),
 * the onMouseDown callback and dragging flag, and
 * the style attributes for modal content.
 */
export function useChangeModalPosition(initialPosition, modalContentRef, modalElementRef, diff) {
  const [x, setX] = useState(initialPosition ? initialPosition.x : undefined);
  const [y, setY] = useState(initialPosition ? initialPosition.y : undefined);
  const [dragging, setDragging] = useState(false);
  const [mouseCoord, setMouseCoord] = useState({});

  const forceUpdate = useForceUpdate();

  useEffect(() => { // observe mutations
    if (!window.MutationObserver || !modalElementRef.current) return () => { };
    const observer = new window.MutationObserver(debounce(forceUpdate)); // forced to calc style
    observer.observe(modalElementRef.current, {
      attributes: true,
      childList: true,
      subtree: true,
    });
    return () => {
      observer.disconnect();
    };
  }, [forceUpdate, modalElementRef]);

  useEffect(() => { // window resize
    if (dragging) return () => { };
    const callback = createResizeWindowCallback(x, y, mouseCoord, setX, setY);
    callback();
    window.addEventListener('resize', callback);
    return () => {
      window.removeEventListener('resize', callback);
    };
  }, [dragging, mouseCoord, x, y]);

  useEffect(() => { // mouse up
    if (!dragging) return () => { };
    const callbackMouseUp = (e) => {
      setDragging(false);
      e.stopPropagation();
      e.preventDefault();
    };
    document.addEventListener('mouseup', callbackMouseUp);
    return () => {
      document.removeEventListener('mouseup', callbackMouseUp);
    };
  }, [dragging]);

  useEffect(() => { // move everywhere
    if (!dragging) return () => { };
    const changePosition = (e) => {
      const { width, height } = getSize();
      let posX = e.pageX - mouseCoord.x;
      let posY = e.pageY - mouseCoord.y;
      if (posX < 0) {
        posX = 0;
      } else if (posX + mouseCoord.width > width) {
        posX = width - mouseCoord.width;
      }
      if (posY < 0) {
        posY = 0;
      } else if (posY + mouseCoord.height > height) {
        posY = height - mouseCoord.height;
      }
      setX(posX); setY(posY);
    };

    const callbackMouseMove = (e) => {
      if (!dragging) return;
      changePosition(e);
      e.stopPropagation();
      e.preventDefault();
    };

    document.addEventListener('mousemove', callbackMouseMove);
    return () => {
      document.removeEventListener('mousemove', callbackMouseMove);
    };
  }, [dragging, mouseCoord]);

  const onMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    if (modalContentRef.current) {
      setDragging(true);
      setMouseCoord({
        x: e.pageX - modalContentRef.current.offsetLeft,
        y: e.pageY - modalContentRef.current.offsetTop,
        width: modalContentRef.current.offsetWidth,
        height: modalContentRef.current.offsetHeight + 1,
      });
    }
    e.stopPropagation();
    e.preventDefault();
  }, [modalContentRef]);

  return {
    x,
    y,
    ...calcStyle(y, modalContentRef, getSize().height, diff),
    dragging,
    onMouseDown,
  };
}

const PH_CLASS_PREFIX = 'modal-ph-';

/**
 * Add modal-open class to body.
 * It also adds a placeholder class to remember who added the modal-open class.
 * This will be useful when it will remove the class.
 * @param {object} win browser window (only for test)
 * @param {object} doc browser document (only for test)
 * @returns The placeholder class.
 */
export function addModalOpenClassToBody(win = window, doc = document) {
  if (win.innerHeight < doc.documentElement.scrollHeight) {
    const phClass = `${PH_CLASS_PREFIX}${getRandomId()}`;
    if (!doc.body.classList.contains('modal-open')) {
      doc.body.classList.add('modal-open');
    }
    doc.body.classList.add(phClass);
    return phClass;
  }
  return undefined;
}

/**
 * Remove modal-open class to body if this is the last modal.
 * If the component is the last modal, there aren't any other 'modal-ph-XXX' classes.
 * @param {string} phClass the placeholder class name. It stats with 'modal-ph-'.
 * @param {object} doc browser document (only for test)
 */
export function removeModalOpenClassToBody(phClass, doc = document) {
  if (phClass !== undefined) {
    doc.body.classList.remove(phClass);
    if (doc.body.className && !doc.body.className.includes(PH_CLASS_PREFIX)) {
      doc.body.classList.remove('modal-open');
    }
  }
}

/**
 * Create a new element as a body child.
 */
export function useBodyModalOpenClass(open) {
  useEffect(() => {
    if (!open) return NOOP;
    const myClass = addModalOpenClassToBody();
    return () => {
      removeModalOpenClassToBody(myClass);
    };
  }, [open]);
}
